/**
 * Kundenauswahl der KIAN-Entwurfsdialoge (Einzel- und Sammelbearbeitung).
 *
 * Ein durchsuchbares Mantine-Select schreibt nach jeder Auswahl — und jedes Mal, wenn sich die
 * Beschriftung der gewählten Option ändert — deren Text ins Suchfeld. Bis v1.388.x war dieses
 * Suchfeld zugleich die Kundensuche. Im Entwurf hieß der Kunde „MBM Techglas GmbH", der
 * Suchtreffer „MBM Techglas GmbH (10107)": Das Feld suchte nach dem Namen, bekam die Fassung mit
 * Nummer, suchte danach, fand nichts, fiel auf den Namen zurück — die Kundennummer blinkte alle
 * 300 ms. In der Sammelbearbeitung verschwand der gewählte Kunde nach der Auswahl ganz.
 *
 * Deshalb: Der Text der aktuellen Auswahl ist keine Suche, und die Beschriftung einer einmal
 * gesehenen Option bleibt, auch wenn sie im nächsten Suchergebnis fehlt.
 */
import { useMemo, useRef, useState } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';

export interface CustomerOption {
  value: string;
  label: string;
}

export function customerOptionLabel(c: { name: string; customerNumber?: string | null }): string {
  return c.customerNumber ? `${c.name} (${c.customerNumber})` : c.name;
}

/** Suchbegriff fürs Backend — leer, solange im Feld nur die gewählte Option steht. */
export function customerSearchTerm(query: string, selectedLabel: string | null): string {
  const q = query.trim();
  if (selectedLabel != null && q === selectedLabel.trim()) return '';
  return q;
}

/**
 * Optionen aus dem Suchergebnis; die gewählte bleibt drin und behält die Beschriftung, die sie
 * schon hatte (`known`), sonst `fallbackLabel`.
 */
export function buildCustomerOptions(
  results: CustomerOption[],
  selectedId: string | null,
  known: ReadonlyMap<string, string>,
  fallbackLabel: string | null,
): CustomerOption[] {
  const opts = results.map((o) => ({ value: o.value, label: known.get(o.value) ?? o.label }));
  if (selectedId && !opts.some((o) => o.value === selectedId)) {
    opts.unshift({ value: selectedId, label: known.get(selectedId) ?? fallbackLabel ?? selectedId });
  }
  return opts;
}

export function useCustomerSearchSelect(input: {
  enabled: boolean;
  selectedId: string | null;
  /** Beschriftung der Auswahl, solange sie in keinem Suchergebnis vorkam (Name aus dem Entwurf) */
  fallbackLabel?: string | null;
}) {
  const [query, setQuery] = useState('');
  const known = useRef(new Map<string, string>());

  const selectedLabel = input.selectedId
    ? (known.current.get(input.selectedId) ?? input.fallbackLabel ?? input.selectedId)
    : null;
  const [term] = useDebouncedValue(customerSearchTerm(query, selectedLabel), 300);

  const customers = useQuery({
    queryKey: ['weclapp-customer-search', term],
    queryFn: () => api.searchWeclappCustomers(term),
    enabled: input.enabled && term.length >= 2,
    staleTime: 30_000,
  });

  const options = useMemo(() => {
    const results = (customers.data?.customers ?? []).map((c) => ({ value: String(c.id), label: customerOptionLabel(c) }));
    const opts = buildCustomerOptions(results, input.selectedId, known.current, input.fallbackLabel ?? null);
    // Erst NACH dem Aufbau merken: So behält die Auswahl die Beschriftung, die das Feld schon zeigt.
    for (const o of opts) if (!known.current.has(o.value)) known.current.set(o.value, o.label);
    return opts;
  }, [customers.data, input.selectedId, input.fallbackLabel]);

  return {
    data: options,
    searchValue: query,
    onSearchChange: setQuery,
    /** Beim Öffnen des Dialogs: Suchfeld leeren */
    reset: () => setQuery(''),
    nothingFoundMessage: term.length < 2 ? 'Mindestens 2 Zeichen' : 'Keine Kunden gefunden',
  };
}
